import { useEffect, useRef, type RefObject } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Restore the scroll position of a scroll container across back/forward
 * navigation, and reset to top on forward pushes.
 *
 * Part of back-navigation context restoration — pressing back should land the
 * user where they left the list, not at the top. See
 * docs/FeatureDesigns/back-navigation-context.md.
 *
 * Keyed by `location.key` (per history entry, not per path), so two visits to
 * the same list restore independently. In-memory for the tab session, which is
 * all `location.key` is valid for anyway (it regenerates on reload).
 *
 * The app scrolls inside `.app-main`, not the window, so pass that element's ref.
 */
export function useScrollRestoration(ref: RefObject<HTMLElement | null>) {
    const location = useLocation();
    const navType = useNavigationType();
    const positions = useRef<Map<string, number>>(new Map());

    // Continuously record the current entry's offset while the user scrolls.
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const key = location.key;
        const onScroll = () => positions.current.set(key, el.scrollTop);
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => el.removeEventListener('scroll', onScroll);
    }, [location.key, ref]);

    // On navigation: restore on back/forward (POP), reset to top on a real page
    // change (PUSH). A REPLACE is an in-page URL update — filters, an expanded
    // chapter (?expand=), a tab — where the scroll position must be KEPT; those
    // used to jump to the top on every toggle.
    // Retry across a few frames so an async list that grows after mount still
    // reaches the saved offset instead of clamping to a too-short page.
    useEffect(() => {
        const el = ref.current;
        if (!el || navType === 'REPLACE') return;
        const target = navType === 'POP' ? (positions.current.get(location.key) ?? 0) : 0;
        let frame = 0;
        let raf = 0;
        const apply = () => {
            el.scrollTop = target;
            if (el.scrollTop < target && frame++ < 20) raf = requestAnimationFrame(apply);
        };
        apply();
        return () => cancelAnimationFrame(raf);
    }, [location.key, navType, ref]);
}
